import { useState } from 'react'
import { Plus, Sparkles, Trash2, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { AiSuggestionChips } from './AiSuggestionChips'
import { CustomerProblemSection } from './CustomerProblemSection'
import type { JtbdData, JtbdPainData, JtbdGainData } from '@ispot/shared'

interface Props {
  jtbd: JtbdData
  index: number
  accountId: string
  projectId: string
  sessionId: string
  onUpdate: (jtbd: JtbdData) => void
  onDelete: () => void
  onSynthesizeProblem: (jtbdId: string) => void
  onRefineInWorkshop?: () => void
}

type Severity = NonNullable<JtbdPainData['severity']>
type Importance = NonNullable<JtbdGainData['importance']>

const SEVERITY_OPTIONS: { value: Severity; label: string }[] = [
  { value: 'high', label: 'Critical' },
  { value: 'medium', label: 'Moderate' },
  { value: 'low', label: 'Minor' },
]
const IMPORTANCE_OPTIONS: { value: Importance; label: string }[] = [
  { value: 'must_have', label: 'Critical' },
  { value: 'expected', label: 'Important' },
  { value: 'nice_to_have', label: 'Nice to have' },
]
const SEVERITY_DOT: Record<Severity, string> = {
  high: 'bg-error',
  medium: 'bg-solution',
  low: 'bg-warm-400',
}
const IMPORTANCE_DOT: Record<Importance, string> = {
  must_have: 'bg-market',
  expected: 'bg-solution',
  nice_to_have: 'bg-warm-400',
}

const editableInputClass = '-ml-1.5 w-full rounded bg-transparent px-1.5 py-0.5 transition-colors hover:bg-warm-100 focus:bg-bg-card focus:shadow-[inset_2px_0_0_var(--color-market),0_0_0_1px_var(--color-warm-300)] focus:outline-none placeholder:text-text-muted'

export function JtbdSectionEditor({ jtbd, index, accountId, projectId, sessionId, onUpdate, onDelete, onSynthesizeProblem, onRefineInWorkshop }: Props) {
  const [newPain, setNewPain] = useState('')
  const [newGain, setNewGain] = useState('')
  const [showProblem, setShowProblem] = useState(false)

  const isPrimary = index === 0
  const canSynthesize = jtbd.pains.length >= 2 && jtbd.gains.length >= 1

  const addPain = (text?: string) => {
    const desc = (text ?? newPain).trim()
    if (!desc) return
    onUpdate({
      ...jtbd,
      pains: [...jtbd.pains, { id: `pain-${Date.now()}`, description: desc, severity: 'medium', evidenceCount: 0 }],
    })
    if (text === undefined) setNewPain('')
  }

  const addGain = (text?: string) => {
    const desc = (text ?? newGain).trim()
    if (!desc) return
    onUpdate({
      ...jtbd,
      gains: [...jtbd.gains, { id: `gain-${Date.now()}`, description: desc, importance: 'expected', evidenceCount: 0 }],
    })
    if (text === undefined) setNewGain('')
  }

  const updatePainSeverity = (id: string, severity: Severity) => {
    onUpdate({ ...jtbd, pains: jtbd.pains.map(x => x.id === id ? { ...x, severity } : x) })
  }
  const updateGainImportance = (id: string, importance: Importance) => {
    onUpdate({ ...jtbd, gains: jtbd.gains.map(x => x.id === id ? { ...x, importance } : x) })
  }
  const updatePainText = (id: string, description: string) => {
    onUpdate({ ...jtbd, pains: jtbd.pains.map(x => x.id === id ? { ...x, description } : x) })
  }
  const updateGainText = (id: string, description: string) => {
    onUpdate({ ...jtbd, gains: jtbd.gains.map(x => x.id === id ? { ...x, description } : x) })
  }
  const removePain = (id: string) => {
    onUpdate({ ...jtbd, pains: jtbd.pains.filter(x => x.id !== id) })
  }
  const removeGain = (id: string) => {
    onUpdate({ ...jtbd, gains: jtbd.gains.filter(x => x.id !== id) })
  }

  return (
    <div className="group/section">
      {/* Eyebrow + JTBD-level actions */}
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-market">
          Job {String(index + 1).padStart(2, '0')}
        </span>
        <div className="flex items-center gap-2 opacity-0 transition-opacity group-hover/section:opacity-100 focus-within:opacity-100">
          {onRefineInWorkshop && (
            <button
              onClick={onRefineInWorkshop}
              className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] text-text-muted transition-colors hover:bg-warm-100 hover:text-text"
              title="Refine this JTBD in the guided workshop"
            >
              <Sparkles className="h-3 w-3" /> Refine in workshop
            </button>
          )}
          {!isPrimary && (
            <button
              onClick={onDelete}
              className="rounded-md p-1 text-text-muted transition-colors hover:bg-error/10 hover:text-error"
              title="Remove this JTBD"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Statement (title) */}
      <textarea
        value={jtbd.title}
        placeholder={isPrimary ? 'State the job in one clear sentence.' : 'Another job this persona has…'}
        onChange={(e) => onUpdate({ ...jtbd, title: e.target.value })}
        rows={2}
        className={cn(
          editableInputClass,
          'mb-3 resize-none font-display text-[19px] font-semibold leading-tight tracking-tight',
        )}
      />

      {/* Dimensions: Functional / Emotional / Social */}
      <dl className="mb-4 grid grid-cols-[96px_1fr] gap-0 border-y border-border-subtle py-1">
        <DimensionRow
          label="Functional"
          value={jtbd.functional || ''}
          placeholder="What outcome does this persona need to accomplish?"
          onChange={(v) => onUpdate({ ...jtbd, functional: v })}
          aiPhase="behaviors"
          aiLabel="Suggest functional"
          accountId={accountId} projectId={projectId} sessionId={sessionId}
        />
        <DimensionRow
          label="Emotional"
          value={jtbd.emotional || ''}
          placeholder="How does the persona want to feel?"
          onChange={(v) => onUpdate({ ...jtbd, emotional: v })}
          accountId={accountId} projectId={projectId} sessionId={sessionId}
        />
        <DimensionRow
          label="Social"
          value={jtbd.social || ''}
          placeholder="How does the persona want to be seen?"
          onChange={(v) => onUpdate({ ...jtbd, social: v })}
          accountId={accountId} projectId={projectId} sessionId={sessionId}
        />
      </dl>

      {/* Pains and Gains */}
      <div className="grid grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-2">

        {/* Pains */}
        <section>
          <div className="mb-2 flex items-baseline justify-between text-[11px] font-semibold uppercase tracking-[0.12em] text-text">
            <span>Pains</span>
            <span className="font-normal normal-case tracking-normal text-text-muted tabular-nums">{jtbd.pains.length}</span>
          </div>
          <ul className="m-0 list-none p-0">
            {jtbd.pains.map(p => {
              const severity = (p.severity || 'medium') as Severity
              return (
                <li key={p.id} className="-mx-2 grid grid-cols-[12px_1fr_max-content_max-content] items-center gap-2 rounded px-2 py-1.5 transition-colors hover:bg-warm-100 group/row border-t border-border-subtle first:border-t-0">
                  <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', SEVERITY_DOT[severity])} />
                  <input
                    type="text"
                    value={p.description}
                    onChange={(e) => updatePainText(p.id, e.target.value)}
                    className={cn(editableInputClass, 'text-[13px]')}
                  />
                  <select
                    value={severity}
                    onChange={(e) => updatePainSeverity(p.id, e.target.value as Severity)}
                    className="cursor-pointer appearance-none rounded bg-transparent px-1.5 pr-5 py-0.5 text-[10.5px] font-medium uppercase tracking-[0.06em] text-text-muted transition-colors hover:bg-warm-200 focus:bg-warm-200 focus:outline-none"
                    style={{
                      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 24 24' fill='none' stroke='%239b9286' stroke-width='2.5'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 4px center',
                    }}
                  >
                    {SEVERITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <button
                    onClick={() => removePain(p.id)}
                    className="rounded p-1 text-text-muted opacity-0 transition-all hover:bg-error/10 hover:text-error group-hover/row:opacity-100 focus:opacity-100"
                    title="Remove pain"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </li>
              )
            })}
          </ul>
          <div className="mt-1 flex items-center gap-1.5 text-[12px] text-text-muted">
            <Plus className="h-3 w-3 shrink-0" />
            <input
              type="text"
              value={newPain}
              onChange={(e) => setNewPain(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addPain()}
              onBlur={() => addPain()}
              placeholder="Add a pain…"
              className="flex-1 rounded bg-transparent px-1 py-0.5 text-text outline-none transition-colors focus:bg-warm-100 placeholder:text-text-muted"
            />
          </div>
          <AiSuggestionChips
            accountId={accountId} projectId={projectId} sessionId={sessionId}
            phaseId="pains" label="Suggest pains"
            onAccept={(text) => addPain(text)}
          />
        </section>

        {/* Gains */}
        <section>
          <div className="mb-2 flex items-baseline justify-between text-[11px] font-semibold uppercase tracking-[0.12em] text-text">
            <span>Gains</span>
            <span className="font-normal normal-case tracking-normal text-text-muted tabular-nums">{jtbd.gains.length}</span>
          </div>
          <ul className="m-0 list-none p-0">
            {jtbd.gains.map(g => {
              const importance = (g.importance || 'expected') as Importance
              return (
                <li key={g.id} className="-mx-2 grid grid-cols-[12px_1fr_max-content_max-content] items-center gap-2 rounded px-2 py-1.5 transition-colors hover:bg-warm-100 group/row border-t border-border-subtle first:border-t-0">
                  <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', IMPORTANCE_DOT[importance])} />
                  <input
                    type="text"
                    value={g.description}
                    onChange={(e) => updateGainText(g.id, e.target.value)}
                    className={cn(editableInputClass, 'text-[13px]')}
                  />
                  <select
                    value={importance}
                    onChange={(e) => updateGainImportance(g.id, e.target.value as Importance)}
                    className="cursor-pointer appearance-none rounded bg-transparent px-1.5 pr-5 py-0.5 text-[10.5px] font-medium uppercase tracking-[0.06em] text-text-muted transition-colors hover:bg-warm-200 focus:bg-warm-200 focus:outline-none"
                    style={{
                      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 24 24' fill='none' stroke='%239b9286' stroke-width='2.5'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 4px center',
                    }}
                  >
                    {IMPORTANCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <button
                    onClick={() => removeGain(g.id)}
                    className="rounded p-1 text-text-muted opacity-0 transition-all hover:bg-error/10 hover:text-error group-hover/row:opacity-100 focus:opacity-100"
                    title="Remove outcome"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </li>
              )
            })}
          </ul>
          <div className="mt-1 flex items-center gap-1.5 text-[12px] text-text-muted">
            <Plus className="h-3 w-3 shrink-0" />
            <input
              type="text"
              value={newGain}
              onChange={(e) => setNewGain(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addGain()}
              onBlur={() => addGain()}
              placeholder="Add an outcome…"
              className="flex-1 rounded bg-transparent px-1 py-0.5 text-text outline-none transition-colors focus:bg-warm-100 placeholder:text-text-muted"
            />
          </div>
          <AiSuggestionChips
            accountId={accountId} projectId={projectId} sessionId={sessionId}
            phaseId="behaviors" label="Suggest outcomes"
            onAccept={(text) => addGain(text)}
          />
        </section>

      </div>

      {/* Customer problem (collapsible) */}
      {(jtbd.customerProblem || canSynthesize) && (
        <div className="mt-4">
          {!showProblem ? (
            <button
              onClick={() => setShowProblem(true)}
              className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] text-text-muted transition-colors hover:bg-warm-100 hover:text-text"
            >
              <Sparkles className="h-3 w-3" />
              {jtbd.customerProblem ? 'Show customer problem' : 'Synthesize customer problem'}
            </button>
          ) : (
            <CustomerProblemSection
              problem={jtbd.customerProblem}
              canSynthesize={canSynthesize}
              onSynthesize={() => onSynthesizeProblem(jtbd.id)}
              onEdit={(statement) =>
                onUpdate({ ...jtbd, customerProblem: { ...jtbd.customerProblem!, statement } })
              }
            />
          )}
        </div>
      )}
    </div>
  )
}

/* ==========================================================================
   Dimension row — one of Functional / Emotional / Social
   ========================================================================== */
interface DimensionRowProps {
  label: string
  value: string
  placeholder: string
  onChange: (value: string) => void
  aiPhase?: string
  aiLabel?: string
  accountId: string
  projectId: string
  sessionId: string
}

function DimensionRow({ label, value, placeholder, onChange, aiPhase, aiLabel, accountId, projectId, sessionId }: DimensionRowProps) {
  return (
    <>
      <dt className="border-t border-dashed border-warm-200 px-0 py-2 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-text-muted first-of-type:border-t-0">
        {label}
      </dt>
      <dd className="m-0 flex items-center gap-2 border-t border-dashed border-warm-200 py-1 first-of-type:border-t-0">
        <input
          type="text"
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={cn(editableInputClass, 'flex-1 text-[13.5px] text-text-secondary')}
        />
        {aiPhase && (
          <AiSuggestionChips
            accountId={accountId} projectId={projectId} sessionId={sessionId}
            phaseId={aiPhase} label={aiLabel || 'Suggest'}
            onAccept={(text) => onChange(text)}
          />
        )}
      </dd>
    </>
  )
}
